import React, { useEffect, useState } from 'react';
import TaskRow from './TaskRow';
import useElapsedSeconds from '../../hooks/useElapsedSeconds';
import { formatClock } from '../tracker/Timer/TimerBar';
import type { TimeEntry } from '../../types/timeEntry';
import type { SearchHit, TodoTask, TodoView } from '../../types/todo';

type TaskListPaneProps = {
  view: TodoView;
  title: string;
  sub: string;
  tasks: TodoTask[];
  today: string;
  cap: number;
  // 「今天」或「明天」的已用额度。两个视图各自 5 条，所以不能写死读 today。
  slotCount: number;
  rolledOver: number;
  onDismissRollover: () => void;
  // 页面持有这个 ref，用来实现「按 n 聚焦添加框」
  addInputRef: React.RefObject<HTMLInputElement | null>;
  capBlocked: boolean;
  error: string | null;
  selectedId: number | null;
  runningEntry: TimeEntry | null;
  onSelect: (task: TodoTask) => void;
  onContextMenu: (task: TodoTask, x: number, y: number) => void;
  onToggle: (task: TodoTask) => void;
  onStar: (task: TodoTask) => void;
  onCreate: (title: string) => void;
  // 收到的是「当前视图未完成任务的新顺序」，合并回全局顺序由页面负责
  onReorder: (orderedIds: number[]) => void;
  onStopTimer: () => void;
  onOpenSidebar: () => void;
  // 这一栏那个常驻搜索框。null = 这个视图不放框（只有待办池放，见 spec §3.1）。
  // 输入的值由页面持有 —— 左栏还有一个框显示同一个词，两处必须同源。
  searchBox: {
    draft: string;
    inputRef: React.RefObject<HTMLInputElement | null>;
    onChange: (value: string) => void;
    onClear: () => void;
  } | null;
  // 服务端回来的搜索态。draft 有字而这里还是 null，就是结果还在路上。
  search: { q: string; meta: Record<string, SearchHit> } | null;
  // 标题这一行右侧的插槽，原来给 WorklogLinkPill 用，那颗胶囊暂停后目前
  // 没人挂。放在这里而不是页面级的 TodoLayout——那样会跟全局导航贴在一起，
  // 看起来像导航项而不是这个列表自己的状态；挂在标题行才是"这一屏正在看的
  // 东西"该有的位置。
  headerExtra?: React.ReactNode;
};

// 空状态是有话可说的地方。「今天」空着和「待办池」空着是两件完全不同的事，
// 前者需要引导去挑事，后者是好消息。共用一句「这里是空的」等于什么都没说。
// Exclude 'trash'：同 VIEW_META，回收站的空状态在 TodoTrash 里自己写。
const EMPTY: Record<Exclude<TodoView, 'trash'>, { line: string; hint: string }> = {
  today: { line: '「今天」还空着。', hint: '去待办池挑最多 5 件今天真的做得完的事。' },
  tomorrow: { line: '「明天」还空着。', hint: '今天塞不下的事，可以先放到这儿。' },
  important: { line: '还没有标记为重要的事。', hint: '在任务上点☆，它就会出现在这里。' },
  backlog: { line: '待办池是空的。', hint: '想到什么先扔进来，不用马上决定哪天做。' },
  recent: { line: '还没有任何任务。', hint: '在上面那一栏敲下第一条。' },
  project: { line: '这个项目下还没有任务。', hint: '在上面那一栏加的任务会自动归到这个项目。' },
};

const TaskListPane: React.FC<TaskListPaneProps> = ({
  view,
  title,
  sub,
  tasks,
  today,
  cap,
  slotCount,
  rolledOver,
  onDismissRollover,
  addInputRef,
  capBlocked,
  error,
  selectedId,
  runningEntry,
  onSelect,
  onContextMenu,
  onToggle,
  onStar,
  onCreate,
  onReorder,
  onStopTimer,
  onOpenSidebar,
  searchBox,
  search,
  headerExtra,
}) => {
  const searching = search !== null;

  const [draft, setDraft] = useState('');
  const [doneOpen, setDoneOpen] = useState(!searching);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);

  // 普通视图里「已完成」那一组是「今天完成的」，最多几条，展开着正好。
  // 搜索结果里它可能是大头，展开会把未完成的那几条挤出屏幕 —— 而那几条
  // 才是你多半要动的。进出搜索态时各翻一次（组件不重挂载，所以光靠上面
  // 那个初始值不够）。
  useEffect(() => {
    setDoneOpen(!searching);
  }, [searching]);

  const elapsed = useElapsedSeconds(runningEntry?.started_at ?? null);

  // 这一行为什么命中。sidecar 里没有这条 id 就不传 —— TaskRow 那边靠
  // search 在不在决定要不要画前缀和片段。
  const hitFor = (task: TodoTask) => {
    const hit = search?.meta[String(task.id)];
    return hit && search ? { q: search.q, hit } : undefined;
  };
  const open = tasks.filter((task) => !task.completed);
  const done = tasks.filter((task) => task.completed);

  // 把拖动的那条从原位置抽出来，插到目标行之前。拖到自己身上是空操作。
  const commitDrop = () => {
    if (!draggingId || !dropTargetId || draggingId === dropTargetId) {
      setDraggingId(null);
      setDropTargetId(null);
      return;
    }

    const ids = open.map((task) => task.id);
    const without = ids.filter((id) => id !== draggingId);
    const at = without.indexOf(dropTargetId);
    without.splice(at === -1 ? without.length : at, 0, draggingId);

    setDraggingId(null);
    setDropTargetId(null);
    onReorder(without);
  };

  const submit = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    const value = draft.trim();
    if (!value) return;
    onCreate(value);
    setDraft('');
  };

  return (
    <main className="flex min-h-0 min-w-0 flex-col bg-todo-list">
      <div className="px-[22px] pb-[10px] pt-[18px]">
        <div className="flex flex-wrap items-start justify-between gap-[10px]">
          <div className="flex flex-wrap items-baseline gap-[10px]">
            {/* 仅窄屏。刻意不做成汉堡：TodoLayout 已渲染全局 Header，它手机上
                自带一个 ☰，两个汉堡并排无法预期哪个打开哪个。显示当前视图名
                让它自解释成「切换这个列表」。 */}
            <button
              type="button"
              onClick={onOpenSidebar}
              aria-label="切换列表"
              className="-ml-1 flex items-center gap-1 rounded-md px-1 py-0.5 text-[21px] font-semibold tracking-tight hover:bg-todo-card2 sm:hidden"
            >
              {title}
              <span aria-hidden="true" className="text-[12px] font-normal text-todo-muted">▾</span>
            </button>
            {/* 窄屏下标题已经由上面那个切换器承担，h1 再渲染一次就是
                「今天 ▾  今天」。所以窄屏藏 h1，sm: 之后藏切换器。 */}
            <h1 className="text-[21px] font-semibold tracking-tight max-sm:hidden">{title}</h1>
            {(view === 'today' || view === 'tomorrow') && (
              <span
                className={[
                  'rounded-[10px] px-2 font-mono text-[11px] font-semibold leading-[18px]',
                  slotCount >= cap - 1
                    ? 'border border-transparent bg-todo-accent2 text-todo-accent'
                    : 'border border-todo-line bg-todo-card text-todo-ink2',
                ].join(' ')}
              >
                {slotCount} / {cap}
              </span>
            )}
          </div>
          {/* 挂在标题行右侧而不是页面级的全局导航栏——这是"这一屏正在看的
              东西"的状态，跟着列表标题走才对，不是跟着导航走。 */}
          {headerExtra}
        </div>
        {sub && <div className="mt-[3px] text-[12px] text-todo-muted">{sub}</div>}
      </div>

      {/* 昨天没做完的任务是被静默退回待办池的。完全不说会让人早上打开时
          「今天是空的、昨天那几条去哪了」；排红催你又正是这个设计要消灭的。
          说一句事实，可以关掉，不带情绪。 */}
      {rolledOver > 0 && (
        <div className="mx-[22px] mb-[10px] flex items-center gap-2 rounded-lg border border-todo-line bg-todo-card2 px-3 py-2 text-[12px] text-todo-ink2">
          <span className="flex-1">
            {rolledOver} 条昨天没做完的已退回待办池，需要的话重新挑进今天。
          </span>
          <button
            type="button"
            onClick={onDismissRollover}
            aria-label="知道了"
            className="flex-none px-1 text-todo-muted hover:text-todo-ink"
          >
            ✕
          </button>
        </div>
      )}

      <div className="px-[22px] pb-[10px] pt-1">
        {/* 常驻搜索框。形状照 Wiki/SearchBar：🔍 + 输入框 +「N 条」+ ✕ 全在
            一个框里 ——「N 条」贴在框里的右端而不是列表上方，它回答的是
            「我刚敲的这个词有没有用」，紧贴输入框才能被立刻看到。 */}
        {searchBox && (
          <label className="mb-2 flex items-center gap-[9px] rounded-lg border border-todo-line bg-todo-card px-3 py-2 focus-within:border-todo-accent">
            <span aria-hidden="true" className="text-[13px] leading-none text-todo-muted">🔍</span>
            <input
              ref={searchBox.inputRef}
              value={searchBox.draft}
              onChange={(event) => searchBox.onChange(event.target.value)}
              placeholder="搜任务、备注与留言"
              aria-label="搜索任务"
              className="min-w-0 flex-1 border-0 bg-transparent text-todo-ink outline-none placeholder:text-todo-muted"
            />
            {searching && (
              <span className="flex-none text-[12px] tabular-nums text-todo-muted">
                {tasks.length} 条
              </span>
            )}
            {searchBox.draft && (
              <button
                type="button"
                onClick={searchBox.onClear}
                title="清除搜索词"
                aria-label="清除搜索词"
                className="flex-none px-0.5 text-[12px] text-todo-muted hover:text-todo-ink"
              >
                ✕
              </button>
            )}
          </label>
        )}

        {/* 搜索态藏掉添加框：这一栏此刻不是一个能往里加东西的清单 —— 加进去
            它属于待办池，而你看的是搜索结果，它会当场消失或不消失，两种都
            解释不通。顺带也消掉了「两个框我该在哪敲」。 */}
        {!searching && (
        <label className="flex items-center gap-[9px] rounded-lg border border-todo-line bg-todo-card px-3 py-[9px] shadow-sm">
          <span className="text-[15px] leading-none text-todo-accent">＋</span>
          <input
            ref={addInputRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={submit}
            placeholder="添加任务"
            className="flex-1 border-0 bg-transparent text-todo-ink outline-none placeholder:text-todo-muted"
          />
          {/* 快捷键不写出来等于不存在 */}
          <kbd className="flex-none rounded border border-todo-line bg-todo-card2 px-1.5 font-mono text-[10px] leading-[16px] text-todo-muted">
            N
          </kbd>
        </label>
        )}

        {/* 超额时不显示后端那句错误。重点是让人做减法，不是报告失败（spec §5.1）。 */}
        {capBlocked && (
          <div className="mt-2 rounded-lg border border-todo-warnLine bg-todo-warn px-3 py-[9px] text-[12px] leading-relaxed text-todo-ink2">
            「今天」已经有 {cap} 条了。
            <b className="text-todo-ink">先想清楚哪一条今天其实做不完</b>
            {' '}—— 点开它、关掉「加入今天」，再加新的。
          </div>
        )}
        {error && <div className="mt-2 text-[12px] text-todo-urgent">{error}</div>}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-[22px] pb-4">
        {open.length === 0 && done.length === 0 && (
          <div className="px-1 py-[30px] text-center text-[13px] leading-loose text-todo-muted">
            {/* 空结果是唯一一个人会盯着看、并且愿意读一行字的时刻，所以这行字
                用来回答「它到底搜了什么」，而不是写「无结果」。回收站那句是
                刻意留的：它是唯一被排除的一类，不说的话搜不到会被当成 bug。 */}
            {searching ? (
              <>
                <div className="text-todo-ink2">没有匹配的任务</div>
                <div>搜的是标题、备注和留言，含已完成和步骤。</div>
                <div>回收站里的不搜 —— 它是独立页面，30 天后自动清。</div>
              </>
            ) : (
              <>
                <div className="text-todo-ink2">{EMPTY[view].line}</div>
                <div>{EMPTY[view].hint}</div>
              </>
            )}
          </div>
        )}

        {open.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            today={today}
            selected={selectedId === task.id}
            running={runningEntry?.task_id === task.id}
            // 搜索结果不是一个可排序的列表：它跨视图又含已完成，
            // 而 Task.reorder! 的前提是「提交的是整份待办列表」，
            // 在这里拖会把视图外任务的相对顺序搞乱。
            draggable={!searching}
            dragging={draggingId === task.id}
            dropBefore={dropTargetId === task.id && draggingId !== task.id}
            onSelect={onSelect}
            onContextMenu={onContextMenu}
            onToggle={onToggle}
            onStar={onStar}
            search={hitFor(task)}
            onDragStart={setDraggingId}
            onDragOverRow={setDropTargetId}
            onDragEnd={() => {
              setDraggingId(null);
              setDropTargetId(null);
            }}
            onDrop={commitDrop}
          />
        ))}

        {done.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setDoneOpen((prev) => !prev)}
              className="mb-[7px] mt-[14px] flex select-none items-center gap-[7px] text-[12px] text-todo-ink2"
            >
              <span
                className={`text-[9px] text-todo-muted transition-transform ${doneOpen ? 'rotate-90' : ''}`}
              >
                ▶
              </span>
              已完成 {done.length}
            </button>
            {doneOpen &&
              done.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  today={today}
                  selected={selectedId === task.id}
                  running={false}
                  draggable={false}
                  dragging={false}
                  dropBefore={false}
                  onSelect={onSelect}
                  onContextMenu={onContextMenu}
                  onToggle={onToggle}
                  onStar={onStar}
                  search={hitFor(task)}
                  onDragStart={() => {}}
                  onDragOverRow={() => {}}
                  onDragEnd={() => {}}
                  onDrop={() => {}}
                />
              ))}
          </>
        )}
      </div>

      {runningEntry && (
        <div className="mx-[22px] mb-[14px] flex items-center gap-[10px] rounded-lg bg-todo-accent2 px-[13px] py-[9px] text-[12.5px]">
          <span className="font-mono text-[15px] font-semibold leading-none tabular-nums text-todo-accent">
            {formatClock(elapsed)}
          </span>
          <span className="min-w-0 flex-1 truncate text-todo-ink2">
            正在计时 · {runningEntry.task_title ?? runningEntry.project_name ?? '未归类计时'}
          </span>
          <button
            type="button"
            onClick={onStopTimer}
            className="rounded-md border-0 bg-todo-accent px-[11px] py-1 text-[12px] text-white"
          >
            停止
          </button>
        </div>
      )}
    </main>
  );
};

export default TaskListPane;
